// Story 7.6 — TanStack infinite-query hook for GET /api/v1/feed.
//
// SPA consumer of the Story 7.5 feed reader endpoint. Cursor-based
// pagination: each page's `next_cursor` (opaque base64 blob carrying the
// cascade seed + served-id exclusions) becomes the next request's
// `?cursor=` param; `null` means the cascade is exhausted. The endpoint
// is auth-optional: signed-in users get their personalized cascade,
// anonymous visitors get the zero-share cascade. `content_items` is
// empty until content is authored (Stories 7.7-7.11 / a manual seed),
// so the page's empty state is expected early on.

import { useInfiniteQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

/** Mirrors server/src/routes/feed.rs::FeedCard. */
export interface FeedCard {
  id: string;
  source_kind: string;
  creator_user_id: string | null;
  creator_handle: string | null;
  creator_display_name: string | null;
  title: string;
  body: string | null;
  media_url: string | null;
  external_url: string | null;
  published_at: string;
  is_pinned: boolean;
  is_promo: boolean;
  source_specific: unknown;
}

/** Mirrors server/src/routes/feed.rs::FeedResponse. */
export interface FeedResponse {
  items: FeedCard[];
  next_cursor: string | null;
  debug: unknown;
}

async function fetchFeedPage(cursor: string | null): Promise<FeedResponse> {
  const url =
    cursor === null
      ? "/api/v1/feed"
      : `/api/v1/feed?cursor=${encodeURIComponent(cursor)}`;
  const response = await fetch(url, { credentials: "include" });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`feed fetch failed (HTTP ${response.status})`);
  }
  return (await response.json()) as FeedResponse;
}

export function useFeed() {
  const query = useInfiniteQuery({
    queryKey: ["feed"],
    queryFn: ({ pageParam }) => fetchFeedPage(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });

  return {
    ...query,
    /** All loaded cards, flattened across pages in render order. */
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
  };
}
